import { useCallback, useEffect, useRef } from 'react';

import qapi from 'views_shared/app/javascript/api';
import qlog from 'views_shared/app/javascript/scripts/qlog';

/**
 * Mailbox + selection sync for inbound `?conversation=` / `?v1_conversation_id=` URLs.
 *
 * Runs on mount and on `popstate` (browser back/forward) so URLs not yet represented
 * in the loaded list slice (different mailbox/tab, Turbolinks + History mismatch on V1,
 * brief loading gaps) still resolve via `findById` instead of leaving the UI out of sync
 * with the address bar.
 */
function useDeepLinkResolver({
  currentReceiverProfileId,
  activeMailbox,
  selectMailbox,
  selectConversation,
  selectedConversationRef,
}) {
  // One always-current snapshot of the inputs, so resolveFromCurrentUrl can stay dep-free
  // and the popstate listener never rebinds. (selectedConversationRef is already a ref —
  // read it through here directly rather than wrapping a ref in another ref.)
  const latest = useRef(null);
  latest.current = {
    currentReceiverProfileId,
    activeMailbox,
    selectMailbox,
    selectConversation,
    selectedConversationRef,
  };

  const fetchGenerationRef = useRef(0);

  const resolveFromCurrentUrl = useCallback(() => {
    if (typeof window === 'undefined') return;

    const { currentReceiverProfileId: profileId, selectedConversationRef: selectedRef } = latest.current;
    if (!profileId) return;

    const params = new URLSearchParams(window.location.search);
    const v1Id = params.get('v1_conversation_id');
    const v2Uuid = params.get('conversation');
    if (!v1Id && !v2Uuid) return;

    const sel = selectedRef?.current;
    if (v2Uuid && sel?.conversation_id === v2Uuid) return;
    if (v1Id && sel && String(sel.migrate_id) === v1Id) return;

    const generation = fetchGenerationRef.current + 1;
    fetchGenerationRef.current = generation;

    (async () => {
      try {
        const result = await qapi.conversations.findById({ receiverProfileId: profileId, v1Id, v2Uuid });

        if (generation !== fetchGenerationRef.current) return;

        const conv = result?.data?.[0];
        if (!conv) return;

        const archived = Boolean(conv.receipt_archived_at);
        const am = latest.current.activeMailbox;
        const visibleInCurrent = (am === 'trash' && archived)
          || (am === 'everything' && !archived)
          || (am === 'focused' && !archived && conv.conversation_mailbox === 'focused');
        if (!visibleInCurrent) latest.current.selectMailbox(archived ? 'trash' : 'everything');
        latest.current.selectConversation(conv);
      } catch (err) {
        qlog.error('Deep-link resolver failed:', err);
      }
    })();
  }, []);

  useEffect(() => {
    resolveFromCurrentUrl();
  }, [currentReceiverProfileId, resolveFromCurrentUrl]);

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;

    window.addEventListener('popstate', resolveFromCurrentUrl);
    return () => window.removeEventListener('popstate', resolveFromCurrentUrl);
  }, [resolveFromCurrentUrl]);
}

export default useDeepLinkResolver;
